import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormGroup,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FC, PropsWithChildren } from "react";
import { useState } from "react";
import type { Control } from "react-hook-form";
import { useForm } from "react-hook-form";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import FormSwitch from "../../components/formComponents/FormSwitch";
import type { OrdersAutomationConfig } from "./automationConfigApi";
import { useOrderAutomationConfig } from "./useOrderAutomationConfig";

export type OrderSettingsFormState = OrdersAutomationConfig;

const Header: FC<PropsWithChildren> = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      marginTop: 1,
    }}
  >
    {children}
  </Typography>
);

interface OrderAutomationRule {
  readonly name: keyof OrdersAutomationConfig;
  readonly label: string;
  readonly conditions: string;
}

const ORDER_AUTOMATION_RULES: readonly OrderAutomationRule[] = [
  {
    name: "toBeCancelledNotification",
    label: "Przypominaj przedstawicielowi o anulacji zamówienia",
    conditions: "Data anulacji < dziś + 14 dni i suma wpłat = 0",
  },
  {
    name: "contactRequiredNotification",
    label: "Przypominaj przedstawicielowi o kontakcie",
    conditions: "Data kontaktu < dziś",
  },
  {
    name: "acceptanceRequiredNotification",
    label: "Przypominaj klientowi o akceptacji potwierdzenia",
    conditions: "Status = Wstępne i potwierdzenie nie jest podpisane",
  },
  {
    name: "paymentRequiredNotification",
    label: "Przypominaj klientowi o zaliczce",
    conditions: "Kwota zaliczki > sumy wpłat i faktura jest wystawiona i data zaliczki < dziś + 2 dni",
  },
  {
    name: "orderCancelledNotification",
    label: "Powiadom klienta o anulacji zamówienia",
    conditions:
      "Data zaliczki < dziś - 7 dni i jest wystawiona faktura i suma wpłat = 0 lub data potwierdzenia < dziś - 14 dni i potwierdzenie jest niepodpisane i status = potwierdzenie wysłane",
  },
  {
    name: "createProformaInvoice",
    label: "Automatycznie wystaw fakturę proforma 4 dni przed terminem zaliczki",
    conditions:
      "Data zaliczki między dziś a dziś + 4 dni, kwota zaliczki > 0, suma wpłat = 0, żadna faktura nie jest wystawiona i status inny niż Anulowane, Anulacja wysłana, Korekta zamówienia, Rozliczone. Faktura proforma na kwotę zaliczki od jednego partnera (najpierw B2BHotels) wysyłana jest na mail do faktur klienta, a opiekun zamówienia dostaje powiadomienie i zadanie „Sprawdź czy wpłacona zaliczka” z terminem na datę zaliczki. Uruchamiane codziennie o 11:00.",
  },
  {
    name: "participantListNotification",
    label: "Przypominaj klientowi o przekazaniu listy uczestników",
    conditions: "termin wyjazdu > dziś + 14 i < dziś + 21 i status = potwierdzone lub do zaliczki lub zaliczkowane",
  },
];

const DescriptionDialog: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>Reguły automatyzacji</DialogTitle>
    <DialogContent>
      {ORDER_AUTOMATION_RULES.map(({ name, label, conditions }) => (
        <div key={name}>
          <Header>{label}</Header>
          <Typography>{conditions}</Typography>
        </div>
      ))}
      <Typography
        variant="body2"
        sx={{
          marginTop: 2,
        }}
      >
        Proces uruchamiany jest w każdy wtorek o 11:00 (z wyjątkiem wystawiania faktur proforma, które odbywa się
        codziennie o 11:00).
      </Typography>
    </DialogContent>
  </Dialog>
);

interface AutomationSwitchProps {
  readonly control: Control<OrderSettingsFormState>;
  readonly rule: OrderAutomationRule;
}

const AutomationSwitch: FC<AutomationSwitchProps> = ({ control, rule }) => (
  <Stack direction="row" sx={{ alignItems: "center" }}>
    <FormSwitch control={control} name={rule.name} label={rule.label} />
    <Tooltip title={rule.conditions} describeChild arrow enterTouchDelay={0}>
      <IconButton size="small" aria-label={`Warunki: ${rule.label}`}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Stack>
);

const OrdersSettings: FC<{
  value: OrdersAutomationConfig;
  onSubmit: (config: OrdersAutomationConfig) => Promise<OrdersAutomationConfig>;
}> = ({ value, onSubmit }) => {
  const { control, handleSubmit, reset } = useForm<OrderSettingsFormState>({
    defaultValues: value,
  });
  const [showDescription, setShowDescription] = useState(false);

  return (
    <Card>
      <CardHeader title="Zamówienia" />
      <CardContent>
        <FormControl component="fieldset" variant="standard">
          <FormGroup>
            {ORDER_AUTOMATION_RULES.map((rule) => (
              <AutomationSwitch key={rule.name} control={control} rule={rule} />
            ))}
          </FormGroup>
        </FormControl>
      </CardContent>
      <CardActions>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <CancelButton onClick={() => reset()} />
          <Button variant="outlined" color="info" onClick={() => setShowDescription(true)}>
            Zobacz reguły
          </Button>
          <AsyncLoadingButton
            onClick={handleSubmit(async (s) => {
              await onSubmit(s);
            })}
          >
            Zapisz
          </AsyncLoadingButton>
        </Stack>
        <DescriptionDialog open={showDescription} onClose={() => setShowDescription(false)} />
      </CardActions>
    </Card>
  );
};

const OrderAutomations = () => {
  const { isLoading, data, updateSettings } = useOrderAutomationConfig();
  if (isLoading || !data) {
    return (
      <Card>
        <CardHeader title="Zamówienia" />
        <CardContent>
          <LinearProgress data-testid="loading-indicator" />
        </CardContent>
      </Card>
    );
  }
  return <OrdersSettings value={data} onSubmit={updateSettings} />;
};

export default OrderAutomations;
